/**
 * Go Green LED — AJAX Cart Drawer
 * Handles add-to-cart interception, drawer open/close,
 * quantity updates, item removal, and live cart rendering.
 */

document.addEventListener('DOMContentLoaded', () => {
  /* ------------------------------------------------------------------ */
  /*  Element references                                                 */
  /* ------------------------------------------------------------------ */

  const cartDrawer  = document.querySelector('.cart-drawer');
  const cartOverlay = document.querySelector('.cart-drawer__overlay');
  const cartClose   = document.querySelector('.cart-drawer__close');
  const cartBody    = document.getElementById('cart-drawer-body');
  const cartFooter  = document.getElementById('cart-drawer-footer');
  const cartCounts  = document.querySelectorAll('.header__cart-count');

  /* ------------------------------------------------------------------ */
  /*  Money formatting                                                   */
  /* ------------------------------------------------------------------ */

  /**
   * Format a price (in cents) using the shop's Shopify money format.
   * Falls back to "${{amount}}" when window.GoGreen.moneyFormat is not set.
   *
   * @param {number} cents — price in cents (e.g. 1999)
   * @returns {string} formatted price string
   */
  function formatMoney(cents) {
    const format =
      (window.GoGreen && window.GoGreen.moneyFormat) || '${{amount}}';

    const amount        = (cents / 100).toFixed(2);
    const amountNoDecimals = Math.floor(cents / 100).toString();

    // Replace Shopify liquid-style tokens
    return format
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
      .replace(/\{\{\s*amount\s*\}\}/g, amount);
  }

  /* ------------------------------------------------------------------ */
  /*  Drawer open / close                                                */
  /* ------------------------------------------------------------------ */

  function openCartDrawer() {
    if (!cartDrawer) return;
    cartDrawer.classList.add('cart-drawer--open');
    cartDrawer.removeAttribute('inert');
    cartDrawer.setAttribute('aria-hidden', 'false');
    if (cartClose) cartClose.removeAttribute('tabindex');
    document.body.style.overflow = 'hidden';
  }

  function closeCartDrawer() {
    if (!cartDrawer) return;
    cartDrawer.classList.remove('cart-drawer--open');
    cartDrawer.setAttribute('inert', '');
    cartDrawer.setAttribute('aria-hidden', 'true');
    if (cartClose) cartClose.setAttribute('tabindex', '-1');
    document.body.style.overflow = '';
  }

  // Close on overlay click
  if (cartOverlay) {
    cartOverlay.addEventListener('click', closeCartDrawer);
  }

  // Close on close-button click
  if (cartClose) {
    cartClose.addEventListener('click', closeCartDrawer);
  }

  /* ------------------------------------------------------------------ */
  /*  AJAX helpers                                                       */
  /* ------------------------------------------------------------------ */

  /**
   * Add a variant to the cart.
   *
   * @param {number|string} variantId
   * @param {number}        quantity
   * @returns {Promise}
   */
  function addToCart(variantId, quantity) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: quantity }),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Add to cart failed');
        return res.json();
      })
      .then(() => refreshCart());
  }

  /**
   * Update the quantity of a line item by its key.
   *
   * @param {string} key      — line item key
   * @param {number} quantity — new quantity (0 to remove)
   * @returns {Promise}
   */
  function updateCartItem(key, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity }),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Cart update failed');
        return res.json();
      })
      .then(() => refreshCart());
  }

  /**
   * Remove a line item from the cart.
   *
   * @param {string} key — line item key
   * @returns {Promise}
   */
  function removeCartItem(key) {
    return updateCartItem(key, 0);
  }

  /* ------------------------------------------------------------------ */
  /*  Cart rendering                                                     */
  /* ------------------------------------------------------------------ */

  /**
   * Fetch the full cart object, re-render items and subtotal,
   * and update the header cart count badge.
   */
  function refreshCart() {
    return fetch('/cart.js', {
      headers: { 'Content-Type': 'application/json' },
    })
      .then((res) => res.json())
      .then((cart) => {
        renderCartItems(cart.items);
        renderCartFooter(cart);
        updateCartCount(cart.item_count);
        // Show footer (with Checkout button) only when there are items
        if (cartFooter) {
          cartFooter.style.display = cart.item_count > 0 ? '' : 'none';
        }
      });
  }

  /**
   * Render the list of line items inside the cart drawer body.
   *
   * @param {Array} items — array of Shopify line item objects
   */
  function renderCartItems(items) {
    if (!cartBody) return;

    if (!items.length) {
      cartBody.innerHTML =
        '<p class="cart-drawer__empty">Your cart is empty.</p>';
      return;
    }

    cartBody.innerHTML = items
      .map(
        (item) => `
        <div class="cart-drawer__item" data-key="${item.key}">
          <div class="cart-drawer__item-image">
            <img
              src="${item.image || ''}"
              alt="${item.title}"
              loading="lazy"
              width="80"
              height="80"
            />
          </div>
          <div class="cart-drawer__item-details">
            <p class="cart-drawer__item-title">${item.title}</p>
            <p class="cart-drawer__item-price">${formatMoney(item.final_line_price)}</p>
            <div class="cart-drawer__qty">
              <button
                class="cart-drawer__qty-btn"
                data-action="decrease"
                data-key="${item.key}"
                data-qty="${item.quantity}"
                aria-label="Decrease quantity"
              >
                &minus;
              </button>
              <span class="cart-drawer__qty-value">${item.quantity}</span>
              <button
                class="cart-drawer__qty-btn"
                data-action="increase"
                data-key="${item.key}"
                data-qty="${item.quantity}"
                aria-label="Increase quantity"
              >
                +
              </button>
            </div>
            <a
              href="#"
              class="cart-drawer__remove"
              data-key="${item.key}"
              aria-label="Remove ${item.title}"
            >
              Remove
            </a>
          </div>
        </div>`
      )
      .join('');
  }

  /**
   * Render the subtotal inside the cart drawer footer.
   *
   * @param {Object} cart — Shopify cart object
   */
  function renderCartFooter(cart) {
    if (!cartFooter) return;

    cartFooter.innerHTML = `
      <div class="cart-drawer__subtotal">
        <span>Subtotal</span>
        <span>${formatMoney(cart.total_price)}</span>
      </div>
      <a href="/checkout" class="cart-drawer__checkout btn btn--primary">
        Checkout
      </a>
    `;
  }

  /**
   * Update every cart-count badge in the header.
   *
   * @param {number} count
   */
  function updateCartCount(count) {
    cartCounts.forEach((el) => {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Event delegation — cart drawer quantity & remove controls           */
  /* ------------------------------------------------------------------ */

  if (cartDrawer) {
    cartDrawer.addEventListener('click', (e) => {
      // Quantity buttons
      const qtyBtn = e.target.closest('.cart-drawer__qty-btn');
      if (qtyBtn) {
        e.preventDefault();
        const key    = qtyBtn.getAttribute('data-key');
        const qty    = parseInt(qtyBtn.getAttribute('data-qty'), 10);
        const action = qtyBtn.getAttribute('data-action');

        if (action === 'increase') {
          updateCartItem(key, qty + 1);
        } else if (action === 'decrease') {
          updateCartItem(key, Math.max(0, qty - 1));
        }
        return;
      }

      // Remove links
      const removeLink = e.target.closest('.cart-drawer__remove');
      if (removeLink) {
        e.preventDefault();
        const key = removeLink.getAttribute('data-key');
        removeCartItem(key);
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Intercept add-to-cart forms                                        */
  /* ------------------------------------------------------------------ */

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[action="/cart/add"]');
    if (!form) return;

    e.preventDefault();

    const variantInput = form.querySelector('[name="id"]');
    const quantityInput = form.querySelector('[name="quantity"]');

    if (!variantInput) return;

    const variantId = variantInput.value;
    const quantity  = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

    addToCart(variantId, quantity).then(() => openCartDrawer());
  });

  /* ------------------------------------------------------------------ */
  /*  Listen for cart:updated from PDP inline scripts                    */
  /* ------------------------------------------------------------------ */

  document.addEventListener('cart:updated', () => {
    refreshCart();
    openCartDrawer();
  });

  /* ------------------------------------------------------------------ */
  /*  Initial cart count on page load                                    */
  /* ------------------------------------------------------------------ */

  refreshCart();
});
